import React from 'react';
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, AuthProvider } from './contexts/AuthContext';

// AIP Design System styles
import './styles/aip/index.css';
import './styles/components/ai-advice.css';
import './styles/components/transcript.css';
import './styles/components/todo.css';
import './styles/components/documents.css';
import './styles/components/options.css';
import './styles/components/pages.css';
import './styles/components/settings.css';
import './styles/components/audio.css';
import './styles/components/participants.css';

// Vision Meeting pages
import MeetingPage from './pages/Meeting';
import StandbyPage from './pages/Standby';

// Vision Manage-new pages
import { ProtectedRoute } from './components/aip';
import LoginPage from './pages/manage-new/LoginPage';
import RegisterPage from './pages/manage-new/RegisterPage';
import RegisterCompletePage from './pages/manage-new/RegisterCompletePage';
import PasswordResetRequestPage from './pages/manage-new/PasswordResetRequestPage';
import ResetPasswordPage from './pages/manage-new/ResetPasswordPage';
import DashboardPage from './pages/manage-new/DashboardPage';
import AiManagerListPage from './pages/manage-new/AiManagerListPage';
import AiManagerCreatePage from './pages/manage-new/AiManagerCreatePage';
import AiManagerEditPage from './pages/manage-new/AiManagerEditPage';
import CheckTemplateListPage from './pages/manage-new/CheckTemplateListPage';
import CheckTemplateCreatePage from './pages/manage-new/CheckTemplateCreatePage';
import CheckTemplateEditPage from './pages/manage-new/CheckTemplateEditPage';
import MinutesListPage from './pages/manage-new/MinutesListPage';
import MinutesDetailPage from './pages/manage-new/MinutesDetailPage';
import SettingsPage from './pages/manage-new/SettingsPage';
import AdminUserListPage from './pages/manage-new/AdminUserListPage';
import AdminUserCreatePage from './pages/manage-new/AdminUserCreatePage';
import AdminUserEditPage from './pages/manage-new/AdminUserEditPage';
import ManageNewLayout from './layouts/ManageNewLayout';

// 初期データ（実際の運用ではAPIから取得）
declare global {
  interface Window {
    __INITIAL_DATA__?: {
      recordingId: string;
      apiToken: string;
      companyId: string;
      companyName: string;
      aiManagers: any[];
      templates: any[];
      meetingGoals: any[];
    };
  }
}

// StandbyPageのラッパー（ナビゲーション用）
const StandbyWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleStart = () => {
    navigate('/meeting');
  };

  return <StandbyPage onStart={handleStart} />;
};

// MeetingPageのラッパー（URLパラメータ取得用）
const MeetingWrapper: React.FC<{
  defaultData: {
    recordingId: string;
    apiToken: string;
    companyId: string;
    companyName: string;
    aiManagers: any[];
    templates: any[];
    meetingGoals: any[];
  };
}> = ({ defaultData }) => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState(true);
  const [initData, setInitData] = React.useState<{
    aiManagers: any[];
    templates: any[];
    companyId: string;
    companyName: string;
  } | null>(null);
  
  // URLパラメータから取得（C#から渡される）
  const token = searchParams.get('token') || defaultData.apiToken;
  const recordingId = searchParams.get('recording_id') || defaultData.recordingId;
  
  // トークンをlocalStorageに保存（API呼び出し用）
  React.useEffect(() => {
    if (token) {
      localStorage.setItem('meeting_api_token', token);
    }
  }, [token]);
  
  // 初期データをAPIから取得
  React.useEffect(() => {
    const fetchInitData = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`/api/manage/init?token=${encodeURIComponent(token)}`);
        if (response.ok) {
          const data = await response.json();
          setInitData({
            aiManagers: data.ai_managers || [],
            templates: data.check_templates || [],
            companyId: String(data.company_id || ''),
            companyName: data.company_name || '',
          });
        }
      } catch (error) {
        console.error('Failed to fetch init data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchInitData();
  }, [token]);
  
  if (isLoading) {
    return (
      <div className="aip-html-body" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}>
        <div>読み込み中...</div>
      </div>
    );
  }
  
  return (
    <MeetingPage
      recordingId={recordingId}
      apiToken={token}
      aiManagers={initData?.aiManagers || defaultData.aiManagers}
      templates={initData?.templates || defaultData.templates}
      meetingGoals={defaultData.meetingGoals}
      companyId={initData?.companyId || defaultData.companyId}
      companyName={initData?.companyName || defaultData.companyName}
    />
  );
};

export function App() {
  // 初期データを取得（サーバーから埋め込まれる想定）
  const initialData = window.__INITIAL_DATA__ || {
    recordingId: `rec_${Date.now()}`,
    apiToken: '',
    companyId: '',
    companyName: '',
    aiManagers: [],
    templates: [],
    meetingGoals: [],
  };

  return (
    <Routes>
      {/* ========================================
          Vision Meeting System Routes
          ======================================== */}

      {/* スタンバイ画面（C#から呼び出される初期画面） */}
      <Route path="/meeting/standby" element={<StandbyWrapper />} />

      {/* 会議画面 - /meeting/ パス（C#から呼び出される） */}
      <Route path="/meeting" element={<MeetingWrapper defaultData={initialData} />} />

      {/* 新規管理画面（ログイン認証方式） - 公開ルート */}
      <Route path="/meeting/manage-new/login" element={<LoginPage />} />
      <Route path="/meeting/manage-new/register" element={<RegisterPage />} />
      <Route path="/meeting/manage-new/register/complete" element={<RegisterCompletePage />} />
      <Route path="/meeting/manage-new/password-reset" element={<PasswordResetRequestPage />} />
      <Route path="/meeting/manage-new/reset-password" element={<ResetPasswordPage />} />
      
      {/* 認証が必要な管理画面ルート */}
      <Route
        path="/meeting/manage-new"
        element={
          <ProtectedRoute>
            <ManageNewLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="ai-managers" element={<AiManagerListPage />} />
        <Route path="ai-managers/new" element={<AiManagerCreatePage />} />
        <Route path="ai-managers/:id" element={<AiManagerEditPage />} />
        <Route path="ai-managers/:id/edit" element={<AiManagerEditPage />} />
        <Route path="check-templates" element={<CheckTemplateListPage />} />
        <Route path="check-templates/new" element={<CheckTemplateCreatePage />} />
        <Route path="check-templates/:id" element={<CheckTemplateEditPage />} />
        <Route path="check-templates/:id/edit" element={<CheckTemplateEditPage />} />
        <Route path="minutes" element={<MinutesListPage />} />
        <Route path="minutes/:id" element={<MinutesDetailPage />} />
        <Route path="admin-users" element={<AdminUserListPage />} />
        <Route path="admin-users/new" element={<AdminUserCreatePage />} />
        <Route path="admin-users/:id" element={<AdminUserEditPage />} />
        <Route path="admin-users/:id/edit" element={<AdminUserEditPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* デフォルトルート */}
      <Route path="/" element={<Navigate to="/meeting/manage-new/login" replace />} />
      <Route path="*" element={<Navigate to="/meeting/manage-new/login" replace />} />
    </Routes>
  );
}

// AppWrapper with AuthProvider
export default function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
